import { Tabbar } from "@/features/tabbar";
import { Workspace, WorkspaceModal } from "@/features/workspace";
import { useTranslation } from "@/hooks";
import { useDatabaseStore } from "@/stores/database";
import { useDataflowStore, useOpenDataflows } from "@/stores/dataflow";
import { useUIStore } from "@/stores/ui";
import type { Tab } from "@/types/tabbar";
import { ActionIcon } from "@mantine/core";
import { IconBook2, IconFolders, IconPlus } from "@tabler/icons-react";
import { Activity, useEffect, useMemo } from "react";
import { Outlet, useNavigate, useParams } from "react-router";
import "./BuildLayout.css";

export function BuildLayout() {
  const { t } = useTranslation();
  const openDataflowIds = useOpenDataflows();
  const openModal = useUIStore((state) => state.openModal);
  const setActiveDataflow = useDataflowStore(
    (state) => state.setActiveDataflow
  );
  const removeDataflow = useDataflowStore((state) => state.removeDataflow);
  const dataflows = useDataflowStore((state) => state.dataflows);
  const getDataflowById = useDatabaseStore((state) => state.getDataflowById);
  const addDataflow = useDataflowStore((state) => state.addDataflow);
  const { dataflowId, projectId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (dataflowId) {
      const dataflowStore = useDataflowStore.getState();
      if (!dataflowStore.dataflows[dataflowId]) {
        const dbDataflow = getDataflowById(dataflowId);
        if (dbDataflow) {
          addDataflow(dataflowId, {
            ...dbDataflow,
            dataflow_name: dbDataflow.dataflow_name || undefined,
            dataflow_description: dbDataflow.dataflow_description || undefined,
            flow_definition_json:
              (dbDataflow.flow_definition_json as any) || [],
          });
        }
      }
      // setActiveDataflow will add the dataflow to openDataflowIds if not already there
      setActiveDataflow(dataflowId);
    }
  }, [dataflowId, projectId, setActiveDataflow, getDataflowById, addDataflow]);

  function handleCloseDataflow(id: string) {
    const dataflow = dataflows[id];
    if (dataflow?.project_id) {
      removeDataflow(id);
    }
    if (id == dataflowId) {
      navigate("/build");
    }
  }

  const openDataflows = openDataflowIds
    .map((id) => dataflows[id])
    .filter(Boolean);

  const tabs = useMemo<Tab[]>(() => {
    const projectsTab: Tab = {
      id: "projects",
      to: "/build",
      icon: IconFolders,
      label: t("projects").projects,
      isClosable: false,
    };

    const dataflowTabs: Tab[] = openDataflows
      .filter((dataflow) => dataflow?.dataflow_id && dataflow?.project_id)
      .map((dataflow) => ({
        id: dataflow.dataflow_id!,
        to: `/build/${dataflow.project_id}/${dataflow.dataflow_id}`,
        icon: IconBook2,
        label: dataflow.dataflow_name || t("workspace").unnamed,
        isClosable: true,
      }));

    return [projectsTab, ...dataflowTabs];
  }, [openDataflows, t]);

  const activeTabMatcher = (pathname: string, tab: Tab): boolean => {
    if (tab.id === "projects") {
      return pathname.startsWith("/build") && !dataflowId;
    }
    return pathname === tab.to || pathname.startsWith(tab.to + "/");
  };

  const actionButton = (
    <ActionIcon
      variant="subtle"
      color="gray"
      bdrs={0}
      w={40}
      h={40}
      onClick={() => openModal(undefined, "workspace")}
      pt={2}
    >
      <IconPlus size={16} color="gray" />
    </ActionIcon>
  );

  return (
    <>
      <Tabbar
        tabs={tabs}
        onClose={handleCloseDataflow}
        actionButton={actionButton}
        activeTabMatcher={activeTabMatcher}
      />
      <main className="build-main">
        {!dataflowId ? (
          <Outlet />
        ) : (
          <>
            {openDataflows.map((dataflow) => {
              if (!dataflow?.dataflow_id || !dataflow?.project_id) return null;
              const isActive = dataflow.dataflow_id === dataflowId;
              return (
                <Activity
                  key={dataflow.dataflow_id}
                  mode={isActive ? "visible" : "hidden"}
                >
                  <Workspace
                    dataflowId={dataflow.dataflow_id}
                    projectId={dataflow.project_id}
                  />
                </Activity>
              );
            })}
            {/* Ensure the current workspace is rendered even if not in openDataflows yet (e.g., on initial navigation) */}
            {dataflowId &&
              !openDataflows.some((df) => df?.dataflow_id === dataflowId) && (
                <Activity key={dataflowId} mode="visible">
                  <Workspace dataflowId={dataflowId} projectId={projectId} />
                </Activity>
              )}
          </>
        )}
      </main>
      <WorkspaceModal defaultProjectId={projectId} />
    </>
  );
}
